// src/pages/AbdomenDiary.tsx
import React, { useState, useEffect } from 'react';
import { Card, Form, Button, InputNumber, Table, message, Space, Typography, Modal, Upload, Image, DatePicker } from 'antd';
import { PlusOutlined, UploadOutlined, DownloadOutlined, EditOutlined } from '@ant-design/icons';
import { fileAPI } from '../utils/api';
import { diaryAPI } from '../utils/api';
import dayjs from 'dayjs';
import type { UploadFile } from 'antd/es/upload/interface';
import { decimalInputNumberProps, formatDecimalDisplay } from '../utils/decimalFormat';
import { appendUploadToFormData, getDownloadFileName, getFilePreviewUrl } from '../utils/fileUrls';
import { formatDiaryDateTime, sortDiaryEntriesNewestFirst } from '../utils/diaryDisplay';

const { Title } = Typography;

export const AbdomenDiary: React.FC = () => {
  const [form] = Form.useForm();
  const [entries, setEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [photoFile, setPhotoFile] = useState<UploadFile | null>(null);

  useEffect(() => {
    loadEntries();
  }, []);

  const normalizeDateValue = (value: any): string | undefined => {
    if (!value) return undefined;
    if (typeof value === 'string') return value;
    if (value?.format) return value.format('YYYY-MM-DD');
    return undefined;
  };

  const loadEntries = async () => {
    try {
      setLoading(true);
      const response = await diaryAPI.getAbdomenEntries();
      setEntries(sortDiaryEntriesNewestFirst((response.data || []) as any[]));
    } catch (error: any) {
      console.error('Error loading abdomen entries:', error);
      message.error('Ошибка загрузки записей');
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditingId(null);
    form.resetFields();
    setPhotoFile(null);
  };

  const openCreateModal = () => {
    setEditingId(null);
    form.setFieldsValue({ entry_date: dayjs() });
    setPhotoFile(null);
    setModalVisible(true);
  };

  const handleEdit = (record: any) => {
    setEditingId(record.id);
    form.setFieldsValue({
      abdomen_cm: record.abdomen_cm,
      entry_date: record.entry_date ? dayjs(record.entry_date) : dayjs(),
    });
    setPhotoFile(null);
    setModalVisible(true);
  };

  const onFinish = async (values: any) => {
    try {
      const formData = new FormData();
      formData.append('abdomen_cm', values.abdomen_cm.toString());
      const normalizedDate = normalizeDateValue(values.entry_date) || dayjs().format('YYYY-MM-DD');
      formData.append('entry_date', normalizedDate);
      if (photoFile) {
        appendUploadToFormData(formData, 'photo', photoFile, 'abdomen.jpg');
      }

      if (editingId) {
        await diaryAPI.updateAbdomenEntry(editingId, formData);
        message.success('Запись успешно обновлена!');
      } else {
        await diaryAPI.createAbdomenEntry(formData);
        message.success('Запись успешно добавлена!');
      }
      closeModal();
      await loadEntries();
    } catch (error: any) {
      console.error('Error saving abdomen entry:', error);
      message.error(error.response?.data?.detail || 'Ошибка сохранения');
    }
  };

  const columns = [
    {
      title: 'Дата и время',
      key: 'datetime',
      render: (_: any, record: any) => formatDiaryDateTime(record),
    },
    {
      title: 'Окружность (см)',
      dataIndex: 'abdomen_cm',
      key: 'abdomen_cm',
      render: (value: number) => `${formatDecimalDisplay(value)} см`,
    },
    {
      title: 'Фото',
      dataIndex: 'photo_url',
      key: 'photo_url',
      render: (url: string, record: any) => {
        if (!url) return '-';
        const handleDownload = async () => {
          try {
            const response = await fileAPI.downloadFile(url);
            const blob = new Blob([response.data as BlobPart]);
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = getDownloadFileName(url, `abdomen_${record.entry_date || 'photo'}`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
          } catch (error) {
            console.error('Error downloading file:', error);
            message.error('Ошибка скачивания файла');
          }
        };
        return (
          <Space>
            <Image
              src={getFilePreviewUrl(url)}
              width={50}
              height={50}
              style={{ objectFit: 'cover', cursor: 'pointer' }}
              preview={true}
            />
            <Button
              type="link"
              size="small"
              icon={<DownloadOutlined />}
              onClick={handleDownload}
            >
              Скачать
            </Button>
          </Space>
        );
      },
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_: any, record: any) => (
        <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small" />
      ),
    },
  ];

  const isMobile = window.innerWidth < 768;

  return (
    <div style={{ padding: isMobile ? '0' : '0' }}>
      <div style={{ marginBottom: isMobile ? 16 : 24 }}>
        <Title level={isMobile ? 3 : 2} style={{ color: '#263238', fontSize: isMobile ? '20px' : '24px' }}>Дневник окружности живота</Title>
      </div>

      <Card style={{ borderRadius: '12px', marginBottom: 24 }}>
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateModal}
            style={{ backgroundColor: '#7CB342', borderColor: '#7CB342' }}
          >
            Добавить запись
          </Button>
        </Space>
      </Card>

      <Card style={{ borderRadius: '12px' }}>
        <Table
          columns={columns}
          dataSource={entries}
          loading={loading}
          rowKey="id"
          pagination={{ pageSize: 20 }}
        />
      </Card>

      <Modal
        title={editingId ? 'Редактировать запись' : 'Добавить запись'}
        open={modalVisible}
        onCancel={closeModal}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            label="Окружность живота (см)"
            name="abdomen_cm"
            rules={[{ required: true, message: 'Введите окружность!' }]}
          >
            <InputNumber
              placeholder="Введите окружность"
              min={50}
              max={150}
              precision={1}
              step={0.1}
              style={{ width: '100%' }}
              size="large"
              {...decimalInputNumberProps}
            />
          </Form.Item>

          <Form.Item label="Фото (опционально)">
            <Upload
              listType="picture-card"
              maxCount={1}
              accept="image/*"
              beforeUpload={() => false}
              fileList={photoFile ? [photoFile] : []}
              onChange={({ file, fileList }) => {
                if (file.status === 'removed' || fileList.length === 0) {
                  setPhotoFile(null);
                  return;
                }
                setPhotoFile(fileList[fileList.length - 1]);
              }}
              onRemove={() => setPhotoFile(null)}
            >
              {!photoFile && <div><UploadOutlined /> Загрузить</div>}
            </Upload>
          </Form.Item>

          <Form.Item label="Дата" name="entry_date" initialValue={dayjs()}>
            <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" style={{ backgroundColor: '#7CB342', borderColor: '#7CB342' }}>
                {editingId ? 'Обновить' : 'Сохранить'}
              </Button>
              <Button onClick={closeModal}>
                Отмена
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
